import { DialogComponent } from "@theme/dialog";
import { CartAddEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";
import {
  getShadeFilter,
  setShadeFilter,
} from "@theme/shade-filter-preference";

class ShadeDrawerComponent extends DialogComponent {
  requiredRefs = ["dialog", "content", "footer"];

  /** @type {Map<string, Document>} */
  #cache = new Map();

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {string | null} */
  #currentProductId = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = (/** @type {CartAddEvent} */ event) => {
    if (event.detail?.data?.didError) return;
    this.close();
  };

  close() {
    this.closeDialog();
  }

  /**
   * Opens the shade drawer and loads product content.
   * @param {string} productUrl - The product URL to fetch content from.
   * @param {string} productId - The product ID.
   */
  async open(productUrl, productId) {
    this.#currentProductId = productId;

    // Show loading state
    this.refs.content.innerHTML =
      '<div class="shade-drawer__loading"><span class="shade-drawer__spinner"></span></div>';
    this.refs.footer.innerHTML = "";
    this.showDialog();

    try {
      let html = this.#cache.get(productUrl);

      if (!html) {
        this.#abortController?.abort();
        this.#abortController = new AbortController();

        const separator = productUrl.includes("?") ? "&" : "?";
        const response = await fetch(
          `${productUrl}${separator}section_id=shade-drawer-content`,
          {
            signal: this.#abortController.signal,
          },
        );

        if (!response.ok)
          throw new Error(`Failed to fetch: ${response.status}`);

        const text = await response.text();
        html = new DOMParser().parseFromString(text, "text/html");
        this.#cache.set(productUrl, html);
      }

      // Clone the cached document so we don't modify the original
      const doc = new DOMParser().parseFromString(
        html.documentElement.outerHTML,
        "text/html",
      );

      const body = doc.querySelector("[data-shade-drawer-body]");
      const footer = doc.querySelector("[data-shade-drawer-footer]");
      const titleSource = doc.querySelector("[data-shade-drawer-title]");

      // A combined-listing swatch loads a connected child product, not the parent.
      const renderedProductId = doc
        .querySelector(".shade-drawer-section[data-product-id]")
        ?.getAttribute("data-product-id");
      if (renderedProductId) this.#currentProductId = renderedProductId;

      if (titleSource && this.refs.title) {
        this.refs.title.textContent = titleSource.getAttribute(
          "data-shade-drawer-title",
        );
      }

      if (body) {
        this.refs.content.innerHTML = body.innerHTML;
      }

      if (footer) {
        this.refs.footer.innerHTML = footer.innerHTML;
      }

      this.#setupSwatchListeners();
      this.#setupFilterListeners();
      this.#setupFooterListeners();
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Shade drawer error:", error);
      this.refs.content.innerHTML =
        '<p style="padding: 20px;">Unable to load product details.</p>';
    }
  }

  /**
   * Sets up change listeners on option radio inputs.
   *
   * `data-variant-id` isn't a reliable gate here: Liquid only sets it when
   * `product_option_value.variant` resolves against whatever combination was
   * selected at initial render, so a color with no variant in the DEFAULT
   * size (e.g. only sold in "Double" while "Standard" is the default) never
   * gets it — leaving that swatch's radio without a listener and inert for
   * the rest of the session. #handleOptionChange resolves the actual variant
   * live from the variants JSON, so every option radio needs a listener.
   */
  #setupSwatchListeners() {
    const swatchInputs = this.refs.content.querySelectorAll(
      'fieldset[data-option-position] input[type="radio"]',
    );

    for (const input of swatchInputs) {
      input.addEventListener("change", () => this.#handleOptionChange());
    }
  }

  /**
   * Reads the currently checked value of every option fieldset in the drawer.
   * @returns {Map<string, string>} option position -> selected value
   */
  #getSelectedByPosition() {
    const fieldsets = this.refs.content.querySelectorAll(
      "fieldset[data-option-position]",
    );

    /** @type {Map<string, string>} */
    const selected = new Map();
    for (const fieldset of fieldsets) {
      const position = fieldset.dataset.optionPosition;
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (position && checked instanceof HTMLInputElement) {
        selected.set(position, checked.value);
      }
    }
    return selected;
  }

  /**
   * Finds the variant matching every currently selected option value.
   * @param {Array<Record<string, unknown>>} variants
   * @param {Map<string, string>} selectedByPosition
   */
  #findMatchingVariant(variants, selectedByPosition) {
    return variants.find((variant) =>
      [...selectedByPosition].every(
        ([position, value]) => variant[`option${position}`] === value,
      ),
    );
  }

  /**
   * Checks the radio matching each of the variant's option values. Used to
   * force every option fieldset (not just the one the shopper clicked) onto
   * a fallback variant.
   * @param {Record<string, unknown>} variant
   */
  #applyVariantSelection(variant) {
    const fieldsets = this.refs.content.querySelectorAll(
      "fieldset[data-option-position]",
    );

    for (const fieldset of fieldsets) {
      const position = fieldset.dataset.optionPosition;
      const value = position ? variant[`option${position}`] : undefined;
      if (typeof value !== "string") continue;

      const radio = [...fieldset.querySelectorAll('input[type="radio"]')].find(
        (candidate) =>
          candidate instanceof HTMLInputElement && candidate.value === value,
      );
      if (radio instanceof HTMLInputElement) radio.checked = true;
    }
  }

  /**
   * Syncs every fieldset's "Selected: X" label to its own currently checked
   * value. Must run after every option change (not only when a fallback
   * variant is applied) — each fieldset owns its own label, so clicking a
   * Color swatch should never touch the Size fieldset's label or vice versa.
   */
  #syncSelectedLabels() {
    const fieldsets = this.refs.content.querySelectorAll(
      "fieldset[data-option-position]",
    );

    for (const fieldset of fieldsets) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      const selectedText = fieldset.querySelector("[data-swatch-selected]");
      if (checked instanceof HTMLInputElement && selectedText) {
        selectedText.textContent = checked.value;
      }
    }
  }

  /**
   * Handles a change on any option radio. Resolves the variant for the full
   * current selection and, if it's out of stock, falls back to the first
   * available variant in the product instead of leaving an unpurchasable
   * combination selected — e.g. clicking Size "Double" when it's out of
   * stock for the selected color jumps to the first available variant.
   */
  #handleOptionChange() {
    const { content } = this.refs;
    const variantsScript = content.querySelector(
      "script[data-product-variants]",
    );

    /** @type {Array<Record<string, unknown>>} */
    let variants = [];
    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch {
        variants = [];
      }
    }

    const selectedByPosition = this.#getSelectedByPosition();
    let variant = variants.length
      ? this.#findMatchingVariant(variants, selectedByPosition)
      : undefined;

    if (variants.length && !variant?.available) {
      const fallback = variants.find((candidate) => candidate.available);
      if (fallback) {
        this.#applyVariantSelection(fallback);
        variant = fallback;
      }
    }

    this.#syncSelectedLabels();

    const variantId = variant?.id != null ? String(variant.id) : undefined;
    const variantName =
      typeof variant?.title === "string" ? variant.title : undefined;
    const isAvailable = Boolean(variant?.available);

    // Update variant name in product info
    const variantNameEl = content.querySelector(
      "[data-shade-drawer-variant-name]",
    );
    if (variantNameEl && variantName) {
      variantNameEl.textContent = variantName;
    }

    // Update the add-to-bag button's variant ID
    const addBtn = this.refs.footer.querySelector("[data-add-to-bag]");
    const qtySelect = this.refs.footer.querySelector("[data-qty-input]");
    const footerBar = this.refs.footer.querySelector(
      ".shade-drawer-footer__bar",
    );
    if (addBtn instanceof HTMLButtonElement && variantId) {
      addBtn.dataset.variantId = variantId;
      addBtn.disabled = !isAvailable;
      if (qtySelect) qtySelect.disabled = !isAvailable;
      if (footerBar) {
        footerBar.classList.toggle(
          "shade-drawer-footer__bar--disabled",
          !isAvailable,
        );
      }
    }

    // The color fieldset is the only one that carries a media id per option.
    const mediaInput = content.querySelector(
      'input[type="radio"]:checked[data-option-media-id]',
    );
    const optionMediaId =
      mediaInput instanceof HTMLElement
        ? mediaInput.dataset.optionMediaId
        : undefined;

    this.#updatePrice(variantId);
    this.#updateVariantImages(optionMediaId);
    this.#recomputeAvailability();
  }

  /**
   * Re-evaluates every option value's availability against the OTHER currently
   * selected values, using the variants JSON already embedded in the drawer.
   * This drawer never re-fetches from the server after the initial load (unlike
   * the PDP variant picker), so changing one option (e.g. Size) would otherwise
   * leave sibling options (e.g. Color) showing stale disabled state from
   * whatever was selected when the drawer opened.
   */
  #recomputeAvailability() {
    const { content } = this.refs;
    if (!(content instanceof HTMLElement)) return;

    const variantsScript = content.querySelector(
      "script[data-product-variants]",
    );
    if (!variantsScript) return;

    /** @type {Array<Record<string, unknown>>} */
    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch {
      return;
    }

    const fieldsets = [
      ...content.querySelectorAll("fieldset[data-option-position]"),
    ];
    if (fieldsets.length < 2) return;

    const selectedByPosition = this.#getSelectedByPosition();

    for (const fieldset of fieldsets) {
      const position = fieldset.dataset.optionPosition;
      if (!position) continue;

      for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
        if (!(input instanceof HTMLInputElement)) continue;

        const isAvailable = variants.some((variant) => {
          for (const [candidatePosition, selectedValue] of
            selectedByPosition) {
            const expected =
              candidatePosition === position ? input.value : selectedValue;
            if (variant[`option${candidatePosition}`] !== expected)
              return false;
          }
          return Boolean(variant.available);
        });

        input.dataset.optionAvailable = String(isAvailable);
        if (isAvailable) {
          input.removeAttribute("aria-disabled");
        } else {
          input.setAttribute("aria-disabled", "true");
        }

        this.#toggleOosState(input, isAvailable);
      }
    }
  }

  /**
   * Keeps each option's out-of-stock treatment in sync as the shopper
   * changes other options. The initial Liquid render already reflects
   * availability for the default selection, but that's static.
   *
   * Color swatches hide out-of-stock options entirely. Button-style options
   * (e.g. Size) stay visible but disabled with a diagonal strikethrough,
   * matching the PDP — `aria-disabled`/`data-option-available` are already
   * kept current by the caller, which the shared disabled styling reacts to,
   * but the strikethrough SVG itself is static Liquid markup and needs to be
   * added/removed here to match.
   * @param {HTMLInputElement} input
   * @param {boolean} isAvailable
   */
  #toggleOosState(input, isAvailable) {
    const label = input.closest("label");
    if (!label) return;

    if (label.classList.contains("variant-option__button-label--has-swatch")) {
      label.classList.toggle("shade-drawer-option--oos", !isAvailable);
      return;
    }

    const existingLine = label.querySelector("svg");
    if (isAvailable) {
      existingLine?.remove();
    } else if (!existingLine) {
      label.insertAdjacentHTML(
        "beforeend",
        '<svg width="100%" height="100%" viewBox="0 0 100 46" preserveAspectRatio="xMidYMid slice"><line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke" /></svg>',
      );
    }
  }

  /**
   * Updates the displayed price using Liquid pre-formatted prices.
   * @param {string} variantId - The variant ID to look up.
   */
  #updatePrice(variantId) {
    const pricesScript = this.refs.content.querySelector(
      "script[data-variant-prices]",
    );
    const priceEl = this.refs.content.querySelector(
      "[data-shade-drawer-price]",
    );
    if (!pricesScript || !priceEl) return;

    try {
      const prices = JSON.parse(pricesScript.textContent);
      if (prices[variantId]) {
        priceEl.textContent = prices[variantId];
      }
    } catch {
      // Silent fail
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   * @param {string | undefined} mediaId - The media ID of the selected option.
   */
  #updateVariantImages(mediaId) {
    if (!mediaId) return;

    const { content } = this.refs;
    if (!(content instanceof HTMLElement)) return;

    const variantImages = content.querySelectorAll(
      "[data-shade-drawer-variant-image]",
    );
    const targetImage = content.querySelector(
      `[data-shade-drawer-variant-image][data-media-id="${mediaId}"]`,
    );
    if (!variantImages.length || !targetImage) return;

    for (const image of variantImages) {
      if (image instanceof HTMLElement) image.hidden = image !== targetImage;
    }
  }

  /**
   * Sets up click listeners on filter buttons.
   */
  #setupFilterListeners() {
    const filterButtons =
      this.refs.content.querySelectorAll("[data-filter-btn]");
    const filterMapScript = this.refs.content.querySelector(
      "script[data-variant-filter-map]",
    );

    if (!filterButtons.length || !filterMapScript) return;

    /** @type {{ variantFilters: Record<string, string|null>, optionValueVariants: Record<string, string[]> }} */
    let filterData;
    try {
      filterData = JSON.parse(filterMapScript.textContent);
    } catch {
      return;
    }

    for (const button of filterButtons) {
      button.addEventListener("click", () => {
        const filterValue = /** @type {HTMLElement} */ (button).dataset
          .filterValue;

        for (const btn of filterButtons) {
          btn.classList.remove("variant-filter__button--active");
          btn.setAttribute("aria-pressed", "false");
        }
        button.classList.add("variant-filter__button--active");
        button.setAttribute("aria-pressed", "true");

        setShadeFilter(filterValue);
        this.#applyFilter(filterValue, filterData);
      });
    }

    this.#restorePreferredFilter(filterButtons, filterData);
  }

  /**
   * Re-applies the filter the shopper picked earlier, so reopening the drawer does not
   * start from the full shade list again. Does nothing when this product has no such
   * filter, which leaves every shade visible as before.
   * @param {NodeListOf<Element>} filterButtons
   * @param {{ variantFilters: Record<string, string|null>, optionValueVariants: Record<string, string[]> }} filterData
   */
  #restorePreferredFilter(filterButtons, filterData) {
    const preferred = getShadeFilter();
    if (!preferred) return;

    const match = Array.from(filterButtons).find(
      (btn) =>
        /** @type {HTMLElement} */ (btn).dataset.filterValue === preferred,
    );
    if (!match) return;

    for (const btn of filterButtons) {
      btn.classList.remove("variant-filter__button--active");
      btn.setAttribute("aria-pressed", "false");
    }
    match.classList.add("variant-filter__button--active");
    match.setAttribute("aria-pressed", "true");

    this.#applyFilter(preferred, filterData);
  }

  /**
   * Applies a filter to show/hide swatches.
   * @param {string} filterValue - The filter value to apply ('all' or a filter name).
   * @param {{ variantFilters: Record<string, string|null>, optionValueVariants: Record<string, string[]> }} filterData
   */
  #applyFilter(filterValue, filterData) {
    const { variantFilters, optionValueVariants } = filterData;
    const swatchLabels =
      this.refs.content.querySelectorAll("[data-swatch-item]");

    for (const label of swatchLabels) {
      const input = label.querySelector("input[data-option-value-id]");
      if (!input) continue;

      const optionValueId = /** @type {HTMLElement} */ (input).dataset
        .optionValueId;
      const variantIds = optionValueVariants[optionValueId] || [];

      if (filterValue === "all") {
        label.classList.remove("variant-filter--hidden");
      } else {
        const passes = variantIds.some(
          (id) => variantFilters[id] === filterValue,
        );
        label.classList.toggle("variant-filter--hidden", !passes);
      }
    }

    // Auto-select first visible swatch if current selection is hidden
    const checkedLabel = this.refs.content.querySelector(
      "label[data-swatch-item]:has(input:checked)",
    );
    if (
      checkedLabel &&
      checkedLabel.classList.contains("variant-filter--hidden")
    ) {
      const firstVisible = this.refs.content.querySelector(
        "label[data-swatch-item]:not(.variant-filter--hidden) input",
      );
      if (firstVisible instanceof HTMLInputElement) {
        firstVisible.checked = true;
        firstVisible.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  /**
   * Sets up quantity and add-to-bag button listeners in the footer.
   */
  #setupFooterListeners() {
    const footer = this.refs.footer;

    const decreaseBtn = footer.querySelector("[data-qty-decrease]");
    const increaseBtn = footer.querySelector("[data-qty-increase]");
    const qtyInput = /** @type {HTMLInputElement | null} */ (
      footer.querySelector("[data-qty-input]")
    );

    if (decreaseBtn && qtyInput) {
      decreaseBtn.addEventListener("click", () => {
        const current = parseInt(qtyInput.value) || 1;
        if (current > 1) qtyInput.value = String(current - 1);
      });
    }

    if (increaseBtn && qtyInput) {
      increaseBtn.addEventListener("click", () => {
        const current = parseInt(qtyInput.value) || 1;
        qtyInput.value = String(current + 1);
      });
    }

    const addBtn = footer.querySelector("[data-add-to-bag]");
    if (addBtn) {
      addBtn.addEventListener("click", () => this.#addToCart());
    }
  }

  /**
   * Submits the selected variant to cart via fetch.
   */
  async #addToCart() {
    const addBtn = /** @type {HTMLButtonElement | null} */ (
      this.refs.footer.querySelector("[data-add-to-bag]")
    );
    const variantId = addBtn?.dataset.variantId;
    const qtyInput = /** @type {HTMLInputElement | null} */ (
      this.refs.footer.querySelector("[data-qty-input]")
    );
    const quantity = parseInt(qtyInput?.value) || 1;

    if (!variantId || !addBtn) return;

    addBtn.disabled = true;
    const originalText = addBtn.textContent;
    addBtn.textContent = Theme.translations.adding || "Adding...";

    try {
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", quantity.toString());

      const cartItems = document.querySelectorAll("cart-items-component");
      const sectionIds = [];
      cartItems.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });
      if (sectionIds.length) {
        formData.append("sections", sectionIds.join(","));
      }

      const config = fetchConfig("javascript", { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: "text/html" },
      });

      const data = await response.json();

      if (data.status) {
        addBtn.textContent = originalText;
        addBtn.disabled = false;
        return;
      }

      // Clear cache so next open gets fresh data
      this.#cache.clear();

      // Close this shade drawer first and wait for it to fully close. Both the
      // shade dialog and the hamburger menu carry `scroll-lock`, but the global
      // html[scroll-lock] flag is a single boolean — if we open the cart drawer
      // before the shade drawer's delayed close runs, that close would clear the
      // lock out from under the cart drawer, leaving the page scrollable behind it.
      await this.closeDialog();

      // The shade drawer is opened from inside the mobile hamburger menu. Fully
      // close that menu (its <details open> and html[scroll-lock]) so it isn't
      // left showing "Close X" behind the cart drawer. Mirrors the single-variant
      // add-to-bag flow in product-card-cta.
      const headerDrawerDetails = document
        .querySelector("header-drawer")
        ?.querySelector("details[open]");
      if (headerDrawerDetails instanceof HTMLDetailsElement) {
        headerDrawerDetails.classList.remove("menu-open");
        headerDrawerDetails.removeAttribute("open");
        headerDrawerDetails
          .querySelector("summary")
          ?.setAttribute("aria-expanded", "false");
      }

      // Notify the rest of the theme (cart count, line items, sections, etc.).
      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: "shade-drawer",
          itemCount: quantity,
          productId: this.#currentProductId ?? undefined,
          sections: data.sections,
        }),
      );

      // Explicitly open the viewport-matching cart drawer. The header renders a
      // cart-drawer-component in both .desktop-navigation and .mobile-navigation;
      // only the viewport-matching parent is visible, so target that one (and
      // don't rely on the cart drawer's optional auto-open setting).
      const isMobile = window.innerWidth < 768;
      const cartDrawer =
        /** @type {(Element & { open: () => void }) | null} */ (
          document.querySelector(
            isMobile
              ? ".mobile-navigation cart-drawer-component"
              : ".desktop-navigation cart-drawer-component",
          ) || document.querySelector("cart-drawer-component")
        );
      if (cartDrawer) cartDrawer.open();
    } catch (error) {
      console.error("Add to cart error:", error);
      addBtn.textContent = originalText;
      addBtn.disabled = false;
    }
  }
}

if (!customElements.get("shade-drawer-component")) {
  customElements.define("shade-drawer-component", ShadeDrawerComponent);
}
